import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiPlus, FiCalendar, FiEdit2, FiX } from "react-icons/fi";
import { toast } from "react-toastify";

import TimeOffService from "src/Services/API/timeoff.service";
import UserService from "src/Services/API/user.service";
import TimeOffTabbar from "../TimeOff/TimeOffTabbar";
import { handleError } from "src/Common/function";
import { formatDate, formatDateRange, dayCount } from "../TimeOff/TimeOff";
import { TimeOffTypeBadge } from "../TimeOff/TimeOffType";
import NewTimeOffModal from "./NewTimeOffModal";
import CancelRequestDialog from "./CancelRequestDialog";
import MyTimeOffDetail from "./MyTimeOffDetail";

// Status pill styling for this view, matching the design spec. Kept local so
// the shared STATUS_META (used by the admin TimeOff page) stays untouched.
const MY_STATUS_META = {
  0: { label: "Pending", className: "bg-[#FEF9C3] text-[#A16207]" },
  1: { label: "Approved", className: "bg-[#DCFCE7] text-[#15803D]" },
  2: { label: "Approved", className: "bg-[#DCFCE7] text-[#15803D]" },
  3: { label: "Rejected", className: "bg-[#FEE2E2] text-[#B91C1C]" },
  10: { label: "Cancelled", className: "bg-[#EEF0F9] text-[#5F6477]" },
};

// Filter tabs for the requester's own view. Unlike the admin FILTER_TABS, this
// includes a Cancelled tab (status 10). `statuses: null` = show everything.
const MY_FILTER_TABS = [
  { key: "all", label: "All", statuses: null },
  { key: "pending", label: "Pending", statuses: [0] },
  { key: "approved", label: "Approved", statuses: [1, 2] },
  { key: "rejected", label: "Rejected", statuses: [3] },
  { key: "cancelled", label: "Cancelled", statuses: [10] },
];

const DEFAULT_MY_TAB = MY_FILTER_TABS[0];
const myTabKeyFromPath = (slug) => (MY_FILTER_TABS.find((t) => t.key === slug) ?? DEFAULT_MY_TAB).key;

// My Time Off — the current user's personal view of their own time-off
// requests. They can create, edit (while pending), cancel (while pending), and
// view details, but cannot approve/reject.
function MyTimeOff() {
  const currentUser = UserService.getCurrentUser();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // active tab is driven by the /my-time-off/:tab route param
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = myTabKeyFromPath(tab);
  const handleTabChange = (key) => navigate(`/my-time-off/${key}`);

  // request whose details are being viewed in the side drawer, or null
  const [detailItem, setDetailItem] = useState(null);
  // create/edit modal: { mode: "create" } or { mode: "edit", item }
  const [modal, setModal] = useState(null);
  // request pending cancellation confirmation, or null
  const [cancelItem, setCancelItem] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [page, setPage] = useState(1);

  const PAGE_SIZE = 10;

  const pendingCount = requests.filter((req) => req.status === 0).length;
  const activeStatuses = MY_FILTER_TABS.find((t) => t.key === activeTab)?.statuses;
  const visibleRequests = activeStatuses ? requests.filter((req) => activeStatuses.includes(req.status)) : requests;

  const totalResults = visibleRequests.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pagedRequests = visibleRequests.slice(pageStart, pageStart + PAGE_SIZE);

  // reset to first page whenever the active tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const loadRequests = () => {
    setLoading(true);
    TimeOffService.getAll()
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const data = response.data;
          const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
          // only the signed-in user's own requests
          setRequests(currentUser?.id ? list.filter((req) => req.user?.id === currentUser.id) : list);
        }
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmCancel = () => {
    if (!cancelItem) return;
    setCancelling(true);
    TimeOffService.cancel(cancelItem.id)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success("Request cancelled");
          setRequests((prev) => prev.map((r) => (r.id === cancelItem.id ? { ...r, status: 10 } : r)));
          setCancelItem(null);
        }
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        setCancelling(false);
      });
  };

  return (
    <div className="w-full py-6 px-5">
      <div className="w-full flex flex-col items-start rounded-xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0)] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] overflow-hidden">
        {/* Header: tabs + new request, sitting inside the card */}
        <div className="w-full flex items-end justify-between gap-4 px-6 pt-4 border-b border-[#E5E8F3]">
          <TimeOffTabbar
            tabs={MY_FILTER_TABS}
            activeTab={activeTab}
            onChange={handleTabChange}
            pendingCount={pendingCount}
          />
          <div className="pb-4">
            <button
              type="button"
              onClick={() => setModal({ mode: "create" })}
              className="flex items-center gap-2 h-8 px-3 rounded-lg bg-[#0065FD] hover:bg-[#0058DB] text-[14px] font-OpenSansSemiBold capitalize tracking-[0.14px] text-white focus:outline-none"
            >
              <FiPlus size={16} className="text-white" />
              New Request
            </button>
          </div>
        </div>

        {loading ? (
          <div className="w-full px-6 py-12 text-[14px] font-OpenSansRegular text-[#5F6477]">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="w-full flex flex-col items-center justify-center gap-4 py-[49px] text-center">
            <div className="flex items-center justify-center w-20 h-20 rounded-full bg-[#EEF0F9]">
              <svg xmlns="http://www.w3.org/2000/svg" width="35" height="36" viewBox="0 0 35 36" fill="none">
                <path
                  d="M8.33333 7.91667V1.25C8.33333 0.559644 8.89298 0 9.58333 0C10.2737 0 10.8333 0.559644 10.8333 1.25V7.91667C10.8333 8.60702 10.2737 9.16667 9.58333 9.16667C8.89298 9.16667 8.33333 8.60702 8.33333 7.91667Z"
                  fill="#5F6477"
                />
                <path
                  d="M21.6667 7.91667V1.25C21.6667 0.559644 22.2263 0 22.9167 0C23.607 0 24.1667 0.559644 24.1667 1.25V7.91667C24.1667 8.60702 23.607 9.16667 22.9167 9.16667C22.2263 9.16667 21.6667 8.60702 21.6667 7.91667Z"
                  fill="#5F6477"
                />
                <path
                  d="M30 19.5833V7.91667C30 7.36413 29.7804 6.83439 29.3896 6.44368C28.9989 6.05298 28.4692 5.83333 27.9167 5.83333H4.58333C4.0308 5.83333 3.50105 6.05298 3.11035 6.44368C2.71965 6.83439 2.5 7.36413 2.5 7.91667V31.25C2.5 31.8025 2.71965 32.3323 3.11035 32.723C3.50105 33.1137 4.0308 33.3333 4.58333 33.3333H17.9167C18.607 33.3333 19.1667 33.893 19.1667 34.5833C19.1667 35.2737 18.607 35.8333 17.9167 35.8333H4.58333C3.36776 35.8333 2.20231 35.3501 1.34277 34.4906C0.483232 33.631 0 32.4656 0 31.25V7.91667C0 6.70109 0.483232 5.53565 1.34277 4.67611C2.20232 3.81656 3.36776 3.33333 4.58333 3.33333H27.9167C29.1322 3.33333 30.2977 3.81657 31.1572 4.67611C32.0168 5.53565 32.5 6.70109 32.5 7.91667V19.5833C32.5 20.2737 31.9404 20.8333 31.25 20.8333C30.5596 20.8333 30 20.2737 30 19.5833Z"
                  fill="#5F6477"
                />
                <path
                  d="M31.25 13.3333C31.9404 13.3333 32.5 13.893 32.5 14.5833C32.5 15.2737 31.9404 15.8333 31.25 15.8333H1.25C0.559644 15.8333 0 15.2737 0 14.5833C0 13.893 0.559644 13.3333 1.25 13.3333H31.25Z"
                  fill="#5F6477"
                />
                <path
                  d="M32.0329 25.3662C32.521 24.8781 33.3123 24.8781 33.8005 25.3662C34.2886 25.8544 34.2886 26.6456 33.8005 27.1338L25.4671 35.4671C24.979 35.9553 24.1877 35.9553 23.6995 35.4671C23.2114 34.979 23.2114 34.1877 23.6995 33.6995L32.0329 25.3662Z"
                  fill="#5F6477"
                />
                <path
                  d="M23.6995 25.3662C24.1877 24.8781 24.979 24.8781 25.4671 25.3662L33.8005 33.6995C34.2886 34.1877 34.2886 34.979 33.8005 35.4671C33.3123 35.9553 32.521 35.9553 32.0329 35.4671L23.6995 27.1338C23.2114 26.6456 23.2114 25.8544 23.6995 25.3662Z"
                  fill="#5F6477"
                />
              </svg>
            </div>
            <div className="flex flex-col items-center gap-2">
              <h3 className="text-[20px] font-OpenSansSemiBold capitalize text-[#1A2332] leading-6">
                No Time Off Requests Yet
              </h3>
              <p className="max-w-[357px] text-[16px] font-OpenSansRegular text-[#5F6477] leading-6">
                You have not submitted any time off requests. When you do, they will appear here for you to track and
                manage.
              </p>
            </div>
          </div>
        ) : visibleRequests.length === 0 ? (
          <div className="w-full px-6 py-12 text-[14px] font-OpenSansRegular text-[#5F6477]">
            No requests in this category.
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#F8F9FE] border-b border-[#E5E8F3]">
                {["Type", "Dates", "Submitted", "Status", "Note"].map((label) => (
                  <th key={label} className="px-6 py-3 text-[12px] leading-[18px] font-OpenSansSemiBold text-[#5F6477]">
                    {label}
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-[12px] leading-[18px] font-OpenSansSemiBold text-[#5F6477]" />
              </tr>
            </thead>
            <tbody>
              {pagedRequests.map((req) => {
                const status = MY_STATUS_META[req.status] || MY_STATUS_META[0];
                const days = dayCount(req.startDate, req.endDate);
                const isPending = req.status === 0;
                return (
                  <tr
                    key={req.id}
                    onClick={() => setDetailItem(req)}
                    className="border-b border-[#E5E8F3] cursor-pointer hover:bg-[#F8F9FE]"
                  >
                    {/* Type */}
                    <td className="px-6 py-4">
                      <TimeOffTypeBadge type={req.type} useShortLabel />
                    </td>
                    {/* Dates */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-[14px] font-InterMedium text-[#171717] leading-5">
                          {formatDateRange(req.startDate, req.endDate)}
                        </span>
                        <span className="text-[14px] font-OpenSansRegular text-[#5F6477] leading-5">
                          {days} {days === 1 ? "day" : "days"}
                        </span>
                      </div>
                    </td>
                    {/* Submitted */}
                    <td className="px-6 py-4 text-[14px] font-OpenSansRegular text-[#5F6477] leading-5">
                      {formatDate(req.createdAt)}
                    </td>
                    {/* Status */}
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-2xl text-[12px] font-OpenSansSemiBold capitalize leading-[18px] tracking-[0.12px] ${status.className}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    {/* Note */}
                    <td className="px-6 py-4 max-w-[200px]">
                      <div className="truncate text-[12px] font-OpenSansRegular text-[#5F6477] leading-[18px] tracking-[0.12px]">
                        {req.notes || "-"}
                      </div>
                    </td>
                    {/* Actions */}
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end items-center gap-3">
                        {isPending ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setModal({ mode: "edit", item: req })}
                              className="flex items-center gap-2 h-8 px-1 rounded-md text-[14px] font-OpenSansSemiBold capitalize tracking-[0.14px] text-[#0065FD] hover:underline"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="13"
                                viewBox="0 0 13 13"
                                fill="none"
                              >
                                <path
                                  d="M12.4994 11.3332C12.7755 11.3332 12.9994 11.557 12.9994 11.8332C12.9994 12.1093 12.7755 12.3332 12.4994 12.3332H6.49938C6.22323 12.3332 5.99938 12.1093 5.99938 11.8332C5.99938 11.557 6.22323 11.3332 6.49938 11.3332H12.4994Z"
                                  fill="#0065FD"
                                />
                                <path
                                  d="M11.3327 1.91536C11.3327 1.6727 11.2361 1.43985 11.0645 1.26823C10.8929 1.0966 10.6601 1 10.4174 1C10.1747 1 9.94185 1.09661 9.77022 1.26823L1.76306 9.27539C1.664 9.3744 1.59138 9.49707 1.55213 9.63151L1.07817 11.2539L2.70121 10.7806C2.83572 10.7414 2.95891 10.6687 3.05798 10.5697L11.0645 2.5625C11.2361 2.39092 11.3327 2.158 11.3327 1.91536ZM12.3327 1.91536C12.3327 2.42322 12.1306 2.91042 11.7715 3.26953L3.76502 11.2767C3.54696 11.4947 3.27724 11.654 2.98116 11.7402L1.06645 12.2988C0.923129 12.3406 0.770948 12.3437 0.626344 12.3066C0.481734 12.2696 0.349769 12.1941 0.244183 12.0885C0.138548 11.9829 0.0631595 11.8504 0.0260841 11.7057C-0.010928 11.5611 -0.00856058 11.409 0.0332455 11.2656L0.591839 9.35091C0.678205 9.05516 0.838116 8.78617 1.05603 8.56836L9.06319 0.561198C9.42235 0.20204 9.90943 4.37772e-06 10.4174 0C10.9253 0 11.4124 0.202037 11.7715 0.561198C12.1306 0.92035 12.3327 1.40748 12.3327 1.91536Z"
                                  fill="#0065FD"
                                />
                                <path
                                  d="M8.14586 1.47964C8.34112 1.28438 8.65763 1.28438 8.85289 1.47964L10.8529 3.47964C11.0482 3.6749 11.0482 3.99141 10.8529 4.18667C10.6576 4.38193 10.3411 4.38193 10.1459 4.18667L8.14586 2.18667C7.9506 1.99141 7.9506 1.6749 8.14586 1.47964Z"
                                  fill="#0065FD"
                                />
                              </svg>
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setCancelItem(req)}
                              className="flex items-center gap-2 h-8 px-1 rounded-md text-[14px] font-OpenSansSemiBold capitalize tracking-[0.14px] text-[#B91C1C] hover:underline"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="9"
                                height="9"
                                viewBox="0 0 9 9"
                                fill="none"
                              >
                                <path
                                  d="M8.14645 0.146447C8.34171 -0.0488155 8.65822 -0.0488155 8.85348 0.146447C9.04874 0.341709 9.04874 0.658216 8.85348 0.853478L0.853478 8.85348C0.658216 9.04874 0.341709 9.04874 0.146447 8.85348C-0.0488155 8.65822 -0.0488155 8.34171 0.146447 8.14645L8.14645 0.146447Z"
                                  fill="#B91C1C"
                                />
                                <path
                                  d="M0.146447 0.146447C0.341709 -0.0488155 0.658216 -0.0488155 0.853478 0.146447L8.85348 8.14645C9.04874 8.34171 9.04874 8.65822 8.85348 8.85348C8.65822 9.04874 8.34171 9.04874 8.14645 8.85348L0.146447 0.853478C-0.0488155 0.658216 -0.0488155 0.341709 0.146447 0.146447Z"
                                  fill="#B91C1C"
                                />
                              </svg>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setDetailItem(req)}
                            className="h-8 px-1 rounded-md text-[14px] font-OpenSansSemiBold capitalize tracking-[0.14px] text-[#7C829A] hover:text-[#0F172A]"
                          >
                            View Details
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && requests.length > 0 && visibleRequests.length > 0 && (
          <div className="w-full flex justify-between items-center px-6 pt-3 pb-4 border-t border-[#E5E8F3]">
            <div className="text-[14px] font-OpenSansRegular text-[#868DA6] leading-5">
              Showing{" "}
              <span className="font-OpenSansSemiBold text-[#1A2332]">{totalResults === 0 ? 0 : pageStart + 1}</span> to{" "}
              <span className="font-OpenSansSemiBold text-[#1A2332]">{pageStart + pagedRequests.length}</span> of{" "}
              <span className="font-OpenSansSemiBold text-[#1A2332]">{totalResults}</span> results
            </div>

            <div className="flex items-center gap-3">
              <button
                disabled={currentPage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3.5 py-2 rounded-lg bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] text-[14px] font-OpenSansSemiBold text-[#5F6477] leading-5 disabled:text-[#C9CEE2]"
              >
                Previous
              </button>

              <div className="flex items-center gap-0.5">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => {
                  const isActive = num === currentPage;
                  return (
                    <button
                      key={num}
                      onClick={() => setPage(num)}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg text-[14px] font-OpenSansSemiBold leading-5 ${
                        isActive ? "bg-[#F5F9FF] text-[#0065FD]" : "text-[#5F6477] hover:bg-[#F5F9FF]"
                      }`}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3.5 py-2 rounded-lg bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] text-[14px] font-OpenSansSemiBold text-[#5F6477] leading-5 disabled:text-[#D4D4D4]"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <MyTimeOffDetail
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onCreateNew={() => {
          setDetailItem(null);
          setModal({ mode: "create" });
        }}
      />

      <NewTimeOffModal
        shown={!!modal}
        editItem={modal?.mode === "edit" ? modal.item : null}
        existingRequests={requests}
        onClose={() => setModal(null)}
        onSaved={() => loadRequests()}
      />

      <CancelRequestDialog
        item={cancelItem}
        submitting={cancelling}
        onConfirm={confirmCancel}
        onCancel={() => !cancelling && setCancelItem(null)}
      />
    </div>
  );
}

export default MyTimeOff;
